import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  TableContainer,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip,
  Pagination,
} from '@mui/material';
import {
  Visibility,
  LocalShipping,
  FilterAltOutlined,
  RefreshOutlined,
  CancelOutlined,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { ShipmentStatus } from '../../types/api';
import { Partner } from '../../types/api';
import { useNotification } from '../../contexts/NotificationContext';

interface Shipment {
  id: number;
  order_id: number;
  status: string;
  item_count?: number;
  order_details?: {
    partner_details?: {
      name?: string;
    };
  };
  created_at: string;
  packed_at?: string | null;
}

const getTodayString = () => {
  const today = new Date();
  return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
};

const ShipmentList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError, confirm } = useNotification();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  // Read initial values from URL query params
  const initialStatus = searchParams.get('status') || '';
  const initialOrderId = searchParams.get('order_id') || '';
  const initialStartDate = searchParams.get('start_date') || getTodayString();
  const initialEndDate = searchParams.get('end_date') || getTodayString();

  // Filter states
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [orderId, setOrderId] = useState<string>(initialOrderId);
  const [status, setStatus] = useState<string>(initialStatus);
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 15;

  // Fetch partners on mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await api.get('/api/partners', { params: { type: 'CUSTOMER' } });
        setPartners(response.data.partners || []);
      } catch (err) {
        console.error('Error fetching partners:', err);
      }
    };
    fetchPartners();
  }, []);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit,
        partner_type: 'CUSTOMER',
      };

      if (selectedPartner) {
        params.partner_id = selectedPartner.id;
      }
      if (orderId) {
        params.order_id = orderId;
      }
      if (status) {
        params.status = status;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const response = await api.get('/api/linked-shipments', { params });
      const data = response.data.data || response.data.shipments || [];
      setShipments(Array.isArray(data) ? data : []);
      setTotal(response.data.meta?.total || 0);
      const totalCount = response.data.meta?.total || 0;
      setTotalPages(Math.ceil(totalCount / limit) || 1);
    } catch (err) {
      console.error('Error fetching shipments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
  }, [page]);

  const handleSearch = () => {
    setPage(1);
    // Sync filters to URL
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (orderId) params.order_id = orderId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedPartner) params.partner_id = String(selectedPartner.id);
    setSearchParams(params, { replace: true });
    fetchShipments();
  };

  const handleReset = () => {
    setSelectedPartner(null);
    setOrderId('');
    setStatus('');
    setStartDate(getTodayString());
    setEndDate(getTodayString());
    setPage(1);
    setSearchParams({}, { replace: true });
    setTimeout(fetchShipments, 0);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleCancelShipment = async (id: number) => {
    const confirmed = await confirm({
      title: 'Cancel Shipment',
      message: 'Are you sure you want to cancel this shipment?',
      confirmText: 'Yes, Cancel',
      cancelText: 'No',
      confirmColor: 'error',
      icon: 'warning',
    });
    if (!confirmed) return;

    try {
      setCancellingId(id);
      // Use customers namespace to match other shipment endpoints and ensure server-side cancel
      await api.post(`/api/linked-shipments/${id}/cancel`);
      showSuccess('Shipment cancelled successfully');
      // Refresh list from server to reflect actual state instead of optimistically removing the row
      await fetchShipments();
    } catch (err) {
      console.error('Error cancelling shipment:', err);
      showError('Failed to cancel shipment');
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case ShipmentStatus.CREATED:
        return <Chip label="Created" color="default" size="small" />;
      case ShipmentStatus.PENDING:
        return <Chip label="Pending" color="warning" size="small" />;
      case ShipmentStatus.PACKED:
        return <Chip label="Packed" color="success" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LocalShipping sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h6" fontWeight="bold">
          Shipments - Packing Queue
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FilterAltOutlined color="action" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={500}>
            Filters
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          {/* Order ID Filter */}
          <TextField
            label="Order ID"
            size="small"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Enter Order ID"
            type="number"
          />

          {/* Partner Filter */}
          <Autocomplete
            options={partners}
            getOptionLabel={(option) => option.name}
            value={selectedPartner}
            onChange={(_, newValue) => setSelectedPartner(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Partner" placeholder="All Partners" size="small" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {/* Status Filter */}
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="PACKED">Packed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>

          {/* Start Date */}
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* End Date */}
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="contained" size="small" onClick={handleSearch} disabled={loading}>
            Search
          </Button>
          <Button variant="outlined" size="small" onClick={handleReset} startIcon={<RefreshOutlined />}>
            Reset
          </Button>
        </Box>
      </Paper>

      {/* Shipments Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Shipment ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Partner</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Items</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : shipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No shipments found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                shipments.map((shipment) => (
                  <TableRow key={shipment.id} hover>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        fontWeight={500} 
                        onClick={() => navigate(`/customers/shipments/${shipment.id}`)}
                        sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                      >
                        #{shipment.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{shipment.order_details?.partner_details?.name || '-'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{shipment.item_count || '-'}</Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(shipment.status)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(shipment.created_at).toLocaleDateString()}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Shipment">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/customers/shipments/${shipment.id}`)}
                          aria-label={`view-shipment-${shipment.id}`}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Cancel Shipment">
                        <IconButton
                          size="small"
                          onClick={() => handleCancelShipment(shipment.id)}
                          aria-label={`cancel-shipment-${shipment.id}`}
                          sx={{ ml: 1 }}
                          color="error"
                          disabled={shipment.status !== ShipmentStatus.PENDING || cancellingId === shipment.id}
                        >
                          <CancelOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {total} shipments
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ShipmentList;
